import type { CostCategory, EstimateStatus } from '@buildbee/contracts';
import { applyBps, lineCostCents, roundCents } from '../common';
import { toCsv } from './csv';

/**
 * Estimate pricing: lines by cost code with quantity x unit cost, per-line markup overriding the
 * estimate default, tax on taxable lines. Optional (alternate) lines are priced but excluded from
 * totals; allowances are included.
 */

export type EstimateLineLike = {
  quantity: number;
  unitCostCents: number;
  markupBps?: number | null;
  taxable?: boolean;
  isOptional?: boolean;
  isAllowance?: boolean;
  costCode?: string | null;
  section?: string | null;
  category?: CostCategory;
  description?: string;
};

export type PricedEstimateLine<T> = T & { costTotalCents: number; priceTotalCents: number };

export { lineCostCents };

/** Cost and sell totals for one estimate line; the line's markup overrides the estimate default. */
export function priceEstimateLine(line: Pick<EstimateLineLike, 'quantity' | 'unitCostCents' | 'markupBps'>, defaultMarkupBps: number) {
  const costTotalCents = lineCostCents(line.quantity, line.unitCostCents);
  const markup = line.markupBps ?? defaultMarkupBps;
  return { costTotalCents, priceTotalCents: costTotalCents + applyBps(costTotalCents, markup) };
}

export function priceLines<T extends EstimateLineLike>(lines: readonly T[], defaultMarkupBps: number): PricedEstimateLine<T>[] {
  return lines.map((l) => ({ ...l, ...priceEstimateLine(l, defaultMarkupBps) }));
}

export type EstimateTotals = {
  costSubtotalCents: number;
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  markupCents: number;
  /** Gross margin on sell price in basis points (markup / price). */
  marginBps: number;
  /** Markup on cost in basis points (markup / cost): what the markup field means. */
  markupOnCostBps: number;
  optionalCents: number;
  allowanceCents: number;
};

export function estimateTotals(
  lines: readonly EstimateLineLike[],
  opts: { markupBps: number; taxRateBps: number },
): EstimateTotals {
  let costSubtotalCents = 0;
  let subtotalCents = 0;
  let taxableCents = 0;
  let optionalCents = 0;
  let allowanceCents = 0;
  for (const line of lines) {
    const t = priceEstimateLine(line, opts.markupBps);
    if (line.isOptional) {
      optionalCents += t.priceTotalCents;
      continue;
    }
    if (line.isAllowance) allowanceCents += t.priceTotalCents;
    costSubtotalCents += t.costTotalCents;
    subtotalCents += t.priceTotalCents;
    if (line.taxable !== false) taxableCents += t.priceTotalCents;
  }
  const taxCents = applyBps(taxableCents, opts.taxRateBps);
  const markupCents = subtotalCents - costSubtotalCents;
  const marginBps = subtotalCents > 0 ? roundCents((markupCents / subtotalCents) * 10_000) : 0;
  const markupOnCostBps = costSubtotalCents > 0 ? roundCents((markupCents / costSubtotalCents) * 10_000) : 0;
  return {
    costSubtotalCents,
    subtotalCents,
    taxCents,
    totalCents: subtotalCents + taxCents,
    markupCents,
    marginBps,
    markupOnCostBps,
    optionalCents,
    allowanceCents,
  };
}

export type CostCodeGroup = { key: string; costCode: string | null; section: string | null; costCents: number; priceCents: number; lines: number };

/** Roll lines up by cost code (or section when no code), for summaries and budgets. */
export function groupByCostCode(lines: readonly EstimateLineLike[], defaultMarkupBps: number): CostCodeGroup[] {
  const groups = new Map<string, CostCodeGroup>();
  for (const line of lines) {
    if (line.isOptional) continue;
    const key = line.costCode?.trim() || `section:${line.section?.trim() || 'General'}`;
    const t = priceEstimateLine(line, defaultMarkupBps);
    const g = groups.get(key) ?? { key, costCode: line.costCode?.trim() || null, section: line.section?.trim() || null, costCents: 0, priceCents: 0, lines: 0 };
    g.costCents += t.costTotalCents;
    g.priceCents += t.priceTotalCents;
    g.lines += 1;
    groups.set(key, g);
  }
  return [...groups.values()].sort((a, b) => (a.costCode ?? '~').localeCompare(b.costCode ?? '~') || (a.section ?? '').localeCompare(b.section ?? ''));
}

export type SectionGroup<T> = { section: string; lines: T[]; priceCents: number };

/** Lines grouped by section in their original order (proposal / PDF layout). */
export function groupBySection<T extends EstimateLineLike & { position?: number }>(lines: readonly T[], defaultMarkupBps: number): SectionGroup<T>[] {
  const out: SectionGroup<T>[] = [];
  const sorted = [...lines].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  for (const line of sorted) {
    const section = line.section?.trim() || 'General';
    let group = out.find((g) => g.section === section);
    if (!group) {
      group = { section, lines: [], priceCents: 0 };
      out.push(group);
    }
    group.lines.push(line);
    if (!line.isOptional) group.priceCents += priceEstimateLine(line, defaultMarkupBps).priceTotalCents;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Versions (revisions) and status
// ---------------------------------------------------------------------------

const REVISION_RE = /^(.*?)-R(\d+)$/;

/** `EST-0004-R3` -> `EST-0004`. */
export function baseEstimateNumber(number: string): string {
  const m = REVISION_RE.exec(number);
  return m ? m[1]! : number;
}

/** Revision index: base number is revision 1, `-R2` is 2, ... */
export function revisionOf(number: string): number {
  const m = REVISION_RE.exec(number);
  return m ? Number(m[2]) : 1;
}

/** Next revision number given every number in the family (`EST-0004`, `EST-0004-R2`, ...). */
export function nextRevisionNumber(baseNumber: string, existing: readonly string[]): string {
  const base = baseEstimateNumber(baseNumber);
  const max = existing.filter((n) => baseEstimateNumber(n) === base).reduce((m, n) => Math.max(m, revisionOf(n)), 1);
  return `${base}-R${max + 1}`;
}

const ESTIMATE_TRANSITIONS: Record<EstimateStatus, readonly EstimateStatus[]> = {
  draft: ['in_review', 'sent', 'accepted', 'rejected', 'superseded'],
  in_review: ['draft', 'sent', 'accepted', 'rejected', 'superseded'],
  sent: ['draft', 'accepted', 'rejected', 'superseded'],
  accepted: ['superseded', 'sent'],
  rejected: ['draft', 'superseded'],
  superseded: [],
};

export function canTransitionEstimate(from: EstimateStatus, to: EstimateStatus): boolean {
  return from === to || ESTIMATE_TRANSITIONS[from].includes(to);
}

/** Accepted and superseded estimates are locked; create a revision to change them. */
export function isEstimateLocked(status: EstimateStatus): boolean {
  return status === 'accepted' || status === 'superseded';
}

// ---------------------------------------------------------------------------
// CSV export
// ---------------------------------------------------------------------------

/** Integer cents as a plain decimal (`123456` -> `1234.56`, `-5` -> `-0.05`), exact, no currency sign. */
export function centsToDecimal(cents: number): string {
  const abs = Math.abs(Math.trunc(cents));
  const s = `${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
  return cents < 0 ? `-${s}` : s;
}

/** Basis points as a plain percent (`1300` -> `13`, `1250` -> `12.5`). */
function bpsToPercent(bps: number): string {
  return String(Number((bps / 100).toFixed(4)));
}

export type EstimateCsvInput = {
  number: string;
  title: string;
  markupBps: number;
  taxRateBps: number;
  lines: readonly (EstimateLineLike & { unit?: string; notes?: string | null; position?: number })[];
};

/**
 * The estimate as a spreadsheet: one row per line (cost and sell, so it is for internal use), then
 * the totals. Optional lines are listed but left out of the totals, as on screen.
 */
export function estimateCsv(estimate: EstimateCsvInput): string {
  const lines = [...estimate.lines].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const priced = priceLines(lines, estimate.markupBps);
  const totals = estimateTotals(lines, { markupBps: estimate.markupBps, taxRateBps: estimate.taxRateBps });
  const yesNo = (v: boolean | undefined) => (v ? 'yes' : 'no');
  const rows: (string | number | null | undefined)[][] = [
    ['Estimate', estimate.number, estimate.title],
    [],
    ['Section', 'Cost code', 'Category', 'Description', 'Quantity', 'Unit', 'Unit cost', 'Markup %', 'Cost', 'Price', 'Taxable', 'Allowance', 'Optional', 'Notes'],
    ...priced.map((l) => [
      l.section ?? '',
      l.costCode ?? '',
      l.category ?? '',
      l.description ?? '',
      String(l.quantity),
      l.unit ?? '',
      centsToDecimal(l.unitCostCents),
      bpsToPercent(l.markupBps ?? estimate.markupBps),
      centsToDecimal(l.costTotalCents),
      centsToDecimal(l.priceTotalCents),
      yesNo(l.taxable !== false),
      yesNo(l.isAllowance),
      yesNo(l.isOptional),
      l.notes ?? '',
    ]),
    [],
    ['Cost', centsToDecimal(totals.costSubtotalCents)],
    ['Markup', centsToDecimal(totals.markupCents)],
    ['Subtotal', centsToDecimal(totals.subtotalCents)],
    [`Tax ${bpsToPercent(estimate.taxRateBps)}%`, centsToDecimal(totals.taxCents)],
    ['Total', centsToDecimal(totals.totalCents)],
  ];
  if (totals.allowanceCents) rows.push(['Allowances included', centsToDecimal(totals.allowanceCents)]);
  if (totals.optionalCents) rows.push(['Optional items not included', centsToDecimal(totals.optionalCents)]);
  return toCsv(rows);
}
